import React, { useState } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Mail, MessageSquare, User } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const form = new FormData();
      form.append('name', formData.name);
      form.append('email', formData.email);
      form.append('message', formData.message);
      
      await axios.post(`${API}/contact`, form);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-primary">
      <Navbar />

      {/* Banner */}
      <section className="bg-brand-primary py-20" data-testid="contact-banner">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h1 className="font-heading text-4xl md:text-5xl font-semibold tracking-tighter text-white mb-6">
            Contact Us
          </h1>
          <p className="font-body text-lg text-white/90">
            We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-24 md:py-32">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <div className="bg-white border border-slate-200 rounded shadow-sm p-8 md:p-12" data-testid="contact-form">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label className="block font-body text-sm font-medium text-slate-700 mb-2">
                  Name
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 outline-none font-body text-sm"
                    placeholder="Your name"
                    data-testid="contact-name-input"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block font-body text-sm font-medium text-slate-700 mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 outline-none font-body text-sm"
                    placeholder="your@email.com"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block font-body text-sm font-medium text-slate-700 mb-2">
                  Message
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-4 top-4 h-5 w-5 text-slate-400" />
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 outline-none font-body text-sm resize-none"
                    placeholder="Your message..."
                    data-testid="contact-message-input"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-brand-accent text-white font-body font-medium rounded hover:bg-brand-accent-hover transition-all duration-300 disabled:opacity-50"
                data-testid="contact-submit-button"
              >
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="mt-12 text-center">
            <p className="font-body text-base text-slate-700 mb-4">
              You can also reach us directly at:
            </p>
            <a href="mailto:info@restoremyword.net" className="font-body text-lg font-medium text-brand-accent hover:text-brand-accent-hover">
              info@restoremyword.net
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;